import { useEffect, useRef } from "react";

interface Band {
  readonly col: readonly [number, number, number];
  readonly amp: number;
  readonly y: number;
  readonly sp: number;
  readonly ph: number;
  readonly w: number;
}

const BANDS: readonly Band[] = [
  { col: [77, 255, 184], amp: 46, y: 0.4, sp: 0.00022, ph: 0.0, w: 150 },
  { col: [58, 167, 255], amp: 62, y: 0.47, sp: 0.00017, ph: 2.1, w: 190 },
  { col: [176, 108, 255], amp: 40, y: 0.55, sp: 0.00013, ph: 4.3, w: 165 },
];

/**
 * Vertical offset of a band at a given x and time.
 *
 * Three summed sines at incommensurate frequencies rather than real noise. It is far
 * cheaper, and the beating between them is what stops the result reading as a looping
 * ribbon — a single sine always does.
 *
 * @param x - Horizontal position in CSS pixels.
 * @param t - Milliseconds since page load.
 * @param b - The band being drawn.
 * @returns Offset in pixels from the band's resting line.
 */
function wave(x: number, t: number, b: Band): number {
  // b.sp is radians per millisecond. The first version multiplied it by 1000 as though
  // it needed scaling into those units, which it did not — the result ran about 700x too
  // fast, roughly 35 oscillations a second. That is strobing, not drifting, and is a
  // genuine photosensitivity hazard rather than a matter of taste.
  //
  // At 0.00022 rad/ms the slowest band completes a cycle in about 29 seconds, which is
  // the pace an aurora actually moves at.
  return (
    Math.sin(x * 0.0042 + t * b.sp + b.ph) * b.amp +
    Math.sin(x * 0.0017 - t * b.sp * 0.64 + b.ph * 1.7) * b.amp * 0.65 +
    Math.sin(x * 0.0091 + t * b.sp * 0.33) * b.amp * 0.22
  );
}

/**
 * Animated aurora over a static starfield.
 *
 * Two canvases: the stars are drawn once per resize and left alone, so only the aurora
 * repaints each frame.
 *
 * @returns The canvas layers.
 */
export function Aurora(): React.JSX.Element {
  const starsRef = useRef<HTMLCanvasElement>(null);
  const auroraRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const starsEl = starsRef.current;
    const auroraEl = auroraRef.current;
    if (!starsEl || !auroraEl) return;

    const sx = starsEl.getContext("2d");
    const ax = auroraEl.getContext("2d");
    if (!sx || !ax) return;

    const reduce = matchMedia("(prefers-reduced-motion: reduce)").matches;
    let w = 0;
    let h = 0;
    let frame = 0;
    let stars: { x: number; y: number; r: number; a: number }[] = [];

    const drawAurora = (t: number) => {
      ax.clearRect(0, 0, w, h);
      // Additive, so overlapping bands glow rather than muddying each other.
      ax.globalCompositeOperation = "lighter";
      for (const b of BANDS) {
        const base = h * b.y;
        for (let i = 0; i < 3; i++) {
          const spread = b.w * (1 + i * 0.85);
          const alpha = 0.1 - i * 0.027;
          if (alpha <= 0) continue;
          const [r, g, bl] = b.col;
          const grad = ax.createLinearGradient(0, base - spread, 0, base + spread * 0.55);
          grad.addColorStop(0, `rgba(${r},${g},${bl},0)`);
          grad.addColorStop(0.5, `rgba(${r},${g},${bl},${alpha})`);
          grad.addColorStop(1, `rgba(${r},${g},${bl},0)`);
          ax.fillStyle = grad;
          ax.beginPath();
          ax.moveTo(0, h);
          for (let x = 0; x <= w; x += 14) ax.lineTo(x, base + wave(x, t, b));
          ax.lineTo(w, h);
          ax.closePath();
          ax.fill();
        }
      }
      ax.globalCompositeOperation = "source-over";
    };

    const resize = () => {
      const dpr = Math.min(devicePixelRatio || 1, 2);
      w = innerWidth;
      h = innerHeight;
      for (const [el, ctx] of [
        [starsEl, sx],
        [auroraEl, ax],
      ] as const) {
        el.width = w * dpr;
        el.height = h * dpr;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      }
      // Density by area, so a phone is not given a desktop's star count.
      const n = Math.round((w * h) / 9000);
      stars = Array.from({ length: n }, () => ({
        x: Math.random() * w,
        y: Math.random() * h * 0.92,
        r: Math.random() * 1.1 + 0.2,
        a: Math.random() * 0.55 + 0.12,
      }));
      sx.clearRect(0, 0, w, h);
      for (const s of stars) {
        sx.globalAlpha = s.a;
        sx.fillStyle = "#cfe0ff";
        sx.beginPath();
        sx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        sx.fill();
      }
      sx.globalAlpha = 1;
      drawAurora(performance.now());
    };

    resize();
    addEventListener("resize", resize, { passive: true });

    if (!reduce) {
      const loop = (t: number) => {
        drawAurora(t);
        frame = requestAnimationFrame(loop);
      };
      frame = requestAnimationFrame(loop);
    }

    return () => {
      removeEventListener("resize", resize);
      cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <>
      <canvas ref={starsRef} className="layer" aria-hidden="true" />
      <canvas ref={auroraRef} className="layer" aria-hidden="true" />
    </>
  );
}
